/* eslint-disable no-restricted-imports */
import { AppTypeId } from '@ps/platform-message-framework/common/AppTypeId';
import { ProductId } from '@ps/platform-message-framework/common/ProductId';
import type { HttpHeaders } from '@ps/web-core-api';
import type { ClientOptions, Middleware, MiddlewareCallbackParams } from 'openapi-fetch';
import baseCreate from 'openapi-fetch';
import type { NonUndefined } from 'utility-types';

import type { Store } from '../types/index';

type InjectedHeader =
  | 'x-fi-ps-app-type-id'
  | 'x-fi-ps-brand-id'
  | 'x-fi-ps-device-id'
  | 'x-fi-ps-install-id'
  | 'x-fi-ps-os-id'
  | 'x-fi-ps-platform-id'
  | 'x-fi-ps-product-id'
  | 'x-fi-ps-site-id'
  | 'x-forwarded-for';

export type ExpandRecursive<T> = T extends object
  ? T extends infer O
    ? { [K in keyof O]: ExpandRecursive<O[K]> }
    : never
  : T;

type HeaderKeys<TObject> = Omit<
  {
    [TKey in keyof TObject as TKey extends string ? Lowercase<TKey> : TKey]: TKey extends string
      ? Lowercase<TKey> extends keyof HttpHeaders
        ? TObject[TKey] extends undefined
          ? TObject[TKey]
          : NonUndefined<HttpHeaders[Lowercase<TKey>]>
        : TObject[TKey]
      : TObject[TKey];
  },
  InjectedHeader
>;

type Parameters<TParameters extends { header: object }> = Omit<TParameters, 'header'> & {
  header: HeaderKeys<TParameters['header']>;
};

type Operation<TOperation extends { parameters: { header: object } }> = {
  parameters: Parameters<TOperation['parameters']>;
} & Omit<TOperation, 'parameters'>;

type OptionalHeaders<TPaths> = ExpandRecursive<{
  [TPath in keyof TPaths]: {
    [TOperation in keyof TPaths[TPath]]: TPaths[TPath][TOperation] extends {
      parameters: { header: object };
    }
      ? Operation<TPaths[TPath][TOperation]>
      : never;
  };
}>;

export function createClient<TPaths extends object>(
  { select, getState }: Store,
  options?: ClientOptions
) {
  const { apiDomain } = select.config.get(getState());
  const client = baseCreate<OptionalHeaders<TPaths>>({ baseUrl: apiDomain, ...options });

  const middleware: Middleware = {
    async onRequest({ request }: MiddlewareCallbackParams) {
      const rootState = getState();
      const { installId, idpToken } = select.session.get(rootState);

      request.headers.set('x-fi-ps-site-id', `${select.cms.getSiteId(rootState)}`);
      request.headers.set('x-fi-ps-device-id', `${select.device.getId(rootState)}`);
      request.headers.set('x-fi-ps-os-id', `${select.device.getOS(rootState)}`);
      request.headers.set('x-fi-ps-brand-id', `${select.cms.getBrandId(rootState)}`);
      request.headers.set('x-fi-ps-platform-id', `${select.device.getPlatform(rootState)}`);
      request.headers.set('x-fi-ps-app-type-id', `${AppTypeId.RAM}`);
      request.headers.set('x-fi-ps-product-id', `${ProductId.UNKNOWN}`);

      if (idpToken) request.headers.set('authorization', `Bearer ${idpToken}`);
      if (installId) request.headers.set('x-fi-ps-install-id', installId);

      return undefined;
    }
  };

  client.use(middleware);

  return client;
}
